import React, { FC, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Visibility, VisibilityOff } from '@mui/icons-material';

interface PartnerLoginModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (partnerId: string, token: string) => Promise<void>;
  isLoading?: boolean;
}

export const PartnerLoginModal: FC<PartnerLoginModalProps> = ({ open, onClose, onSubmit, isLoading = false }) => {
  const [partnerId, setPartnerId] = useState('');
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [errors, setErrors] = useState<{ partnerId?: string; token?: string }>({});

  const handleSubmit = useCallback(async () => {
    const newErrors: { partnerId?: string; token?: string } = {};

    if (!partnerId.trim()) {
      newErrors.partnerId = 'Partner ID is required';
    }

    if (!token.trim()) {
      newErrors.token = 'Token is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      return;
    }

    setErrors({});

    return await onSubmit(partnerId.trim(), token.trim());
  }, [partnerId, token, onSubmit]);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setPartnerId('');
      setToken('');
      setErrors({});
      setShowToken(false);
      onClose();
    }
  }, [isLoading, onClose]);

  const handleToggleTokenVisibility = useCallback(() => {
    setShowToken((prev) => !prev);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant='h6' component='div' fontWeight='bold'>
          Partner Login
        </Typography>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          disabled={isLoading}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ pt: 2 }}>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Please enter your partner credentials to access the dashboard.
          </Typography>
          <TextField
            fullWidth
            label='Partner ID'
            value={partnerId}
            onChange={(e) => {
              setPartnerId(e.target.value);
              if (errors.partnerId) {
                setErrors((prev) => ({ ...prev, partnerId: undefined }));
              }
            }}
            error={!!errors.partnerId}
            helperText={errors.partnerId}
            disabled={isLoading}
            sx={{ mb: 3 }}
            autoFocus
          />
          <TextField
            fullWidth
            label='Partner Token'
            type={showToken ? 'text' : 'password'}
            value={token}
            onChange={(e) => {
              setToken(e.target.value);
              if (errors.token) {
                setErrors((prev) => ({ ...prev, token: undefined }));
              }
            }}
            error={!!errors.token}
            helperText={errors.token}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle token visibility'
                    onClick={handleToggleTokenVisibility}
                    edge='end'
                    disabled={isLoading}
                  >
                    {showToken ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isLoading} color='inherit'>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Logging in...' : 'Login'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
